import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect } from 'react'
import { Controller, Resolver, useForm } from 'react-hook-form'
import * as yup from 'yup'

import type {
  TrackingPolicyRule,
  TrackingPolicyRuleFormValues,
} from './types'
import { Button, InputField } from '../../common/designs'

interface Props {
  initialRule?: TrackingPolicyRule
  isSaving: boolean
  onSubmit: (rule: TrackingPolicyRule) => Promise<void>
  onCancel?: () => void
}

const formSchema = yup
  .object({
    in_process_name: yup.string().defined(),
    in_title: yup.string().defined(),
    in_url: yup.string().defined(),
  })
  .test(
    'at-least-one-condition',
    'Add at least one condition',
    ({ in_process_name, in_title, in_url }) =>
      Boolean(in_process_name?.trim() || in_title?.trim() || in_url?.trim()),
  )

const TrackingPolicyRuleForm = ({ initialRule, isSaving, onSubmit, onCancel }: Props) => {
  const { control, handleSubmit, reset } = useForm<TrackingPolicyRuleFormValues>({
    resolver: yupResolver(formSchema) as Resolver<TrackingPolicyRuleFormValues>,
    defaultValues: {
      in_process_name: initialRule?.in_process_name ?? '',
      in_title: initialRule?.in_title ?? '',
      in_url: initialRule?.in_url ?? '',
    },
  })

  useEffect(() => {
    reset({
      in_process_name: initialRule?.in_process_name ?? '',
      in_title: initialRule?.in_title ?? '',
      in_url: initialRule?.in_url ?? '',
    })
  }, [initialRule, reset])

  const submitForm = async (values: TrackingPolicyRuleFormValues) => {
    await onSubmit({
      in_process_name: values.in_process_name.trim() || null,
      in_title: values.in_title.trim() || null,
      in_url: values.in_url.trim() || null,
    })
  }

  return (
    <form className='space-y-3' onSubmit={handleSubmit(submitForm)}>
      <Controller
        name='in_process_name'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputField
            size='s'
            label='Process name'
            placeholder='e.g. excel.exe'
            autoComplete='off'
            error={error?.message}
            {...field}
          />
        )}
      />
      <Controller
        name='in_title'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputField
            size='s'
            label='Window or tab title'
            placeholder='e.g. Accounts'
            autoComplete='off'
            error={error?.message}
            {...field}
          />
        )}
      />
      <Controller
        name='in_url'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <InputField
            size='s'
            label='URL'
            placeholder='e.g. test.com'
            autoComplete='off'
            error={error?.message}
            {...field}
          />
        )}
      />
      <div className='flex gap-2'>
        <Button size='s' type='submit' loading={isSaving}>
          Save
        </Button>
        {onCancel && (
          <Button size='s' variant='white' type='button' onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  )
}

export default TrackingPolicyRuleForm
